import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatMoney } from "@/lib/api";

export const goToCheckout = async (path, setBusy) => {
  setBusy(true);
  try {
    const { data } = await api.post(path);
    window.location.href = data.checkout_url;
  } catch (e) {
    toast.error(formatApiErrorDetail(e.response?.data?.detail));
    setBusy(false);
  }
};

const PackageCard = ({ p, i }) => {
  const [busy, setBusy] = useState(false);
  const monthly = p.billing === "monthly";
  return (
    <article data-testid={`package-card-${p.id}`} className="card-tech rise flex flex-col rounded-2xl p-6 sm:p-8" style={{ animationDelay: `${i * 70}ms` }}>
      <p className="font-mono-tech text-[11px] uppercase tracking-widest text-cyan-400">{monthly ? "Monthly plan" : "One-time project"}</p>
      <h3 data-testid={`package-name-${p.id}`} className="mt-3 text-xl font-semibold text-white">{p.name}</h3>
      <p className="mt-4 font-display text-4xl font-bold text-white">
        <span data-testid={`package-price-${p.id}`}>{formatMoney(p.price)}</span>
        {monthly && <span className="ml-1 text-base font-medium text-slate-400">/month</span>}
      </p>
      <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-400">{p.description}</p>
      <button onClick={() => goToCheckout(`/packages/${p.id}/checkout`, setBusy)} disabled={busy} data-testid={`package-buy-button-${p.id}`}
        className="btn-glow mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-cyan-400 px-6 py-3 text-sm font-semibold text-[#07090E] hover:bg-cyan-300 disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Buy
      </button>
    </article>
  );
};

export const PackagesSection = () => {
  const [packages, setPackages] = useState(null);
  useEffect(() => { api.get("/packages").then((r) => setPackages(r.data)).catch(() => setPackages([])); }, []);
  if (!packages || packages.length === 0) return null;
  const groups = [["One-time projects", packages.filter((p) => p.billing === "one_time")], ["Monthly care plans", packages.filter((p) => p.billing === "monthly")]];
  return (
    <section data-testid="packages-section" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-12">
      <p className="mb-3 font-mono-tech text-xs uppercase tracking-[0.25em] text-cyan-400">// packages_&_pricing</p>
      <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">Packages & Pricing</h2>
      <p className="mt-3 max-w-2xl text-slate-400">Clear, fixed prices in USD. Pay securely through Stripe and we'll reach out within 1 business day to kick off.</p>
      {groups.filter(([, list]) => list.length).map(([title, list]) => (
        <div key={title} className="mt-12">
          <h3 className="mb-6 text-lg font-semibold text-slate-200">{title}</h3>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{list.map((p, i) => <PackageCard key={p.id} p={p} i={i} />)}</div>
        </div>
      ))}
    </section>
  );
};
